export interface EmploymentPeriod {
  startDate: string;
  endDate?: string;
  employerName?: string;
  formId?: string | null;
  needsReview?: boolean;
  status?: string;
}

export interface OverlapResult {
  period: EmploymentPeriod;
  isExactMatch: boolean;
}

/**
 * Find existing employment periods that overlap with a candidate period.
 *
 * Two periods overlap when startA <= endB AND startB <= endA.
 * If a period has no endDate it is treated as open-ended (ongoing employment).
 */
export function findOverlappingPeriods(
  candidate: EmploymentPeriod,
  existing: EmploymentPeriod[]
): OverlapResult[] {
  if (!candidate.startDate) return [];

  const startA = new Date(candidate.startDate);
  const endA = candidate.endDate ? new Date(candidate.endDate) : null;

  const overlaps: OverlapResult[] = [];

  for (const period of existing) {
    if (!period.startDate) continue;

    const startB = new Date(period.startDate);
    const endB = period.endDate ? new Date(period.endDate) : null;

    // An open-ended period (no end date) extends indefinitely into the future
    const effectiveEndA = endA ?? new Date('9999-12-31');
    const effectiveEndB = endB ?? new Date('9999-12-31');

    const doesOverlap = startA <= effectiveEndB && startB <= effectiveEndA;

    if (doesOverlap) {
      const isExactMatch =
        candidate.startDate === period.startDate && candidate.endDate === period.endDate;

      overlaps.push({ period, isExactMatch });
    }
  }

  return overlaps;
}
